import React, { useState, useContext, useEffect } from "react";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import { GlobalContext } from "../context/Provider";
import henceforthApi from "../utils/henceforthApi";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { Button } from "../components/ui/button";
import { Check, Loader2 } from "lucide-react";

const ReactQuill =
  typeof window === "object" ? require("react-quill") : () => false;

const PoliciesEditPage: React.FC = () => {
  const match: any = useMatch("/policies/:type/edit");
  const params = useParams();
  const navigate = useNavigate();
  const policyType = match?.params?.type || params.type || "";

  const { authState, handleError } = useContext(GlobalContext);
  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [state, setState] = useState({
    created_at: "",
    image_url: "",
    title: "",
    type: policyType,
    updated_at: "",
    _id: "",
  });

  const formatDefaultTitle = (type: string) => {
    return type
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const initialiseData = async () => {
    try {
      setFetching(true);
      const apiRes = await henceforthApi.Policies.byType(policyType);
      const obj =
        Array.isArray(apiRes.data) && apiRes.data.length
          ? apiRes.data[0]
          : apiRes.data;
      if (obj) {
        setState((prev) => ({
          ...prev,
          ...obj,
        }));
        setDescription(obj.description || "");
        setTitle(obj.title || formatDefaultTitle(policyType));
      } else {
        setTitle(formatDefaultTitle(policyType));
      }
    } catch (error: any) {
      handleError(error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    initialiseData();
  }, [policyType]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: any = {
      type: policyType,
      title: title.trim() || formatDefaultTitle(policyType),
      description: description,
      language: "ENGLISH",
    };

    if (state.image_url) {
      data["image_url"] = state.image_url;
    }

    setLoading(true);
    henceforthApi.Policies.addEditPolicy(data)
      .then((res: any) => {
        Swal.fire({
          title: "Content Updated",
          text: "Policy content updated successfully",
          icon: "success",
          confirmButtonColor: "#006747",
          confirmButtonText: "OK",
        }).then(() => {
          navigate(`/policies/${policyType}`);
        });
      })
      .catch((err: any) => {
        Swal.fire({
          title: "Error!",
          text: err?.response?.body?.error_description || "Failed to update content",
          icon: "error",
          confirmButtonColor: "#006747",
          confirmButtonText: "Close",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const displayTitle = formatDefaultTitle(policyType);

  return (
    <div className="w-full space-y-3 pb-8">
      {/* ── Edit Form Card ── */}
      <div className="bg-white border border-slate-200/80 rounded-xl shadow-2xs overflow-hidden">
        {fetching ? (
          <div className="flex flex-col items-center justify-center min-h-[360px] text-slate-400 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-[#006747]" />
            <p className="text-xs font-medium">Loading content editor...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-5">
            {/* Rich Text Editor */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Policy Content <span className="text-rose-500">*</span>
              </label>
              <div className="border border-slate-300 rounded-xl overflow-hidden focus-within:border-[#006747] focus-within:ring-2 focus-within:ring-[#006747]/20 transition bg-white">
                <ReactQuill
                  theme="snow"
                  value={description}
                  onChange={setDescription}
                  placeholder={`Write ${displayTitle} content here...`}
                // className="min-h-[320px]"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="pt-4 border-t border-slate-100 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5">
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto text-xs h-9"
                onClick={() => window.history.back()}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="w-full sm:w-auto text-xs h-9"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-3.5 h-3.5 mr-1.5" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PoliciesEditPage;
